import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { TrendingUp, Calendar, ArrowUp, Clock, Plus, X, FlaskConical, History, Trash2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const PortalLeaveForecast = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastYear, setLastYear] = useState(null);
  const [showLastYear, setShowLastYear] = useState(false);
  const [loadingLastYear, setLoadingLastYear] = useState(false);
  const [whatIfLeaves, setWhatIfLeaves] = useState([]);
  const [showWhatIf, setShowWhatIf] = useState(false);
  const [newLeave, setNewLeave] = useState({ start: '', end: '' });
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/employee-portal/leave-forecast`, { headers });
        setData(res.data);
      } catch (err) { console.error('Leave forecast load failed:', err); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const toggleLastYear = async () => {
    if (showLastYear) { setShowLastYear(false); return; }
    if (lastYear) { setShowLastYear(true); return; }
    setLoadingLastYear(true);
    try {
      const res = await axios.get(`${API}/api/employee-portal/leave-forecast/comparison`, { headers });
      setLastYear(res.data);
      setShowLastYear(true);
    } catch (err) { console.error('Leave forecast comparison load failed:', err); }
    finally { setLoadingLastYear(false); }
  };

  // Calculate what-if days per month
  const whatIfByMonth = useMemo(() => {
    const byMonth = {};
    for (const lv of whatIfLeaves) {
      const start = new Date(lv.start + 'T00:00:00');
      const end = new Date(lv.end + 'T00:00:00');
      const d = new Date(start);
      while (d <= end) {
        // Count every calendar day, including weekends/dayoffs — dayoffs within
        // a vacation period are consumed as leave (matches leave salary logic).
        const mo = d.getMonth() + 1;
        byMonth[mo] = (byMonth[mo] || 0) + 1;
        d.setDate(d.getDate() + 1);
      }
    }
    return byMonth;
  }, [whatIfLeaves]);

  const totalWhatIfDays = Object.values(whatIfByMonth).reduce((a, b) => a + b, 0);

  // Compute adjusted forecast with what-if leaves
  const adjustedForecast = useMemo(() => {
    if (!data?.forecast) return [];
    let cumulativeDeduction = 0;
    return data.forecast.map(m => {
      cumulativeDeduction += (whatIfByMonth[m.month] || 0);
      return {
        ...m,
        balance_what_if: Math.round((m.balance_approved_only - cumulativeDeduction) * 10) / 10,
      };
    });
  }, [data, whatIfByMonth]);

  const addWhatIfLeave = () => {
    if (!newLeave.start || !newLeave.end) return;
    if (newLeave.start > newLeave.end) return;
    setWhatIfLeaves(prev => [...prev, { ...newLeave, id: Date.now() }]);
    setNewLeave({ start: '', end: '' });
  };

  const removeWhatIfLeave = (id) => {
    setWhatIfLeaves(prev => prev.filter(l => l.id !== id));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-16 text-gray-400 text-sm">Unable to load forecast data</div>;
  }

  const hasPending = data.pending_leaves_total > 0;
  const hasWhatIf = whatIfLeaves.length > 0;

  // Build chart data
  const chartData = adjustedForecast.map(m => {
    const point = {
      name: m.month_name,
      'Approved Only': m.balance_approved_only,
    };
    if (hasPending) point['With Pending'] = m.balance_with_pending;
    if (hasWhatIf) point['What-If'] = m.balance_what_if;
    if (showLastYear && lastYear) {
      const lyMonth = lastYear.months.find(lm => lm.month === m.month);
      if (lyMonth) point[`${lastYear.year} Actual`] = lyMonth.balance;
    }
    return point;
  });

  const endOfYear = adjustedForecast[adjustedForecast.length - 1];

  // Compute the fractional offset (0=top, 1=bottom) where the zero line falls,
  // so gradients can shade blue/amber/violet above zero and red below zero.
  const allChartValues = chartData.flatMap(d => {
    const vals = [d['Approved Only']];
    if (hasPending) vals.push(d['With Pending']);
    if (hasWhatIf) vals.push(d['What-If']);
    return vals;
  }).filter(v => v != null && !isNaN(v));
  const dataMax = allChartValues.length ? Math.max(...allChartValues) : 0;
  const dataMin = allChartValues.length ? Math.min(...allChartValues) : 0;
  let zeroOffset;
  if (dataMax <= 0) zeroOffset = 0;            // entirely negative → all red
  else if (dataMin >= 0) zeroOffset = 1;       // entirely positive → no red
  else zeroOffset = dataMax / (dataMax - dataMin);

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-leave-forecast">
      {/* Header */}
      <div className="flex items-center gap-2 px-1">
        <TrendingUp className="h-5 w-5 text-sky-600" />
        <h2 className="text-base font-bold text-gray-900">Leave Balance Forecast</h2>
        <Badge variant="outline" className="text-[10px] ml-auto">{data.year}</Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-2">
        {data.carry_forward != null && data.carry_forward !== 0 && (
          <Card className="border-0 shadow-sm" data-testid="carry-forward-card">
            <CardContent className="p-3 text-center">
              <ArrowUp className="h-4 w-4 text-violet-500 mx-auto mb-1" />
              <p className={`text-lg font-bold ${data.carry_forward < 0 ? 'text-red-600' : 'text-violet-700'}`}>{data.carry_forward}</p>
              <p className="text-[9px] text-gray-400">Carry Forward</p>
            </CardContent>
          </Card>
        )}
        <Card className="border-0 shadow-sm" data-testid="current-balance-card">
          <CardContent className="p-3 text-center">
            <Calendar className="h-4 w-4 text-sky-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-sky-700">{data.current_balance}</p>
            <p className="text-[9px] text-gray-400">Current Balance</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="accrual-card">
          <CardContent className="p-3 text-center">
            <ArrowUp className="h-4 w-4 text-emerald-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-emerald-700">+{data.monthly_accrual}</p>
            <p className="text-[9px] text-gray-400">Monthly Accrual</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="pending-card">
          <CardContent className="p-3 text-center">
            <Clock className="h-4 w-4 text-amber-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-amber-700">{data.pending_leaves_total}</p>
            <p className="text-[9px] text-gray-400">Pending Days</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 px-1">
        <Button
          size="sm"
          variant={showWhatIf ? "default" : "outline"}
          className={`flex-1 text-xs gap-1.5 ${showWhatIf ? 'bg-violet-600 hover:bg-violet-700' : ''}`}
          onClick={() => setShowWhatIf(!showWhatIf)}
          data-testid="whatif-toggle-btn"
        >
          <FlaskConical className="h-3.5 w-3.5" /> What-If Planner
        </Button>
        <Button
          size="sm"
          variant={showLastYear ? "default" : "outline"}
          className={`flex-1 text-xs gap-1.5 ${showLastYear ? 'bg-teal-600 hover:bg-teal-700' : ''}`}
          onClick={toggleLastYear}
          disabled={loadingLastYear}
          data-testid="lastyear-toggle-btn"
        >
          <History className="h-3.5 w-3.5" /> {loadingLastYear ? 'Loading...' : `${data.year - 1} Compare`}
        </Button>
      </div>

      {/* What-If Simulator */}
      {showWhatIf && (
        <Card className="border-0 shadow-sm border-l-4 border-l-violet-500" data-testid="whatif-section">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-violet-600" />
              <h3 className="text-xs font-bold text-violet-700 uppercase tracking-wider">What-If Planner</h3>
              {hasWhatIf && (
                <Badge className="ml-auto text-[9px] bg-violet-100 text-violet-700 border-violet-200">
                  -{totalWhatIfDays} days
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-gray-500">Add hypothetical leave dates to see how they impact your balance</p>

            {/* Add leave form */}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-[10px] text-gray-500 font-medium">From</label>
                <Input
                  type="date"
                  value={newLeave.start}
                  onChange={e => setNewLeave({ ...newLeave, start: e.target.value })}
                  className="h-8 text-xs"
                  data-testid="whatif-start-date"
                />
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-gray-500 font-medium">To</label>
                <Input
                  type="date"
                  value={newLeave.end}
                  onChange={e => setNewLeave({ ...newLeave, end: e.target.value })}
                  className="h-8 text-xs"
                  data-testid="whatif-end-date"
                />
              </div>
              <Button
                size="sm"
                className="h-8 px-3 bg-violet-600 hover:bg-violet-700"
                onClick={addWhatIfLeave}
                disabled={!newLeave.start || !newLeave.end || newLeave.start > newLeave.end}
                data-testid="whatif-add-btn"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Listed hypothetical leaves */}
            {whatIfLeaves.length > 0 && (
              <div className="space-y-1.5">
                {whatIfLeaves.map(lv => {
                  const s = new Date(lv.start + 'T00:00:00');
                  const e = new Date(lv.end + 'T00:00:00');
                  let days = 0;
                  const d = new Date(s);
                  while (d <= e) { days++; d.setDate(d.getDate() + 1); }
                  return (
                    <div key={lv.id} className="flex items-center gap-2 bg-violet-50 rounded-lg px-3 py-1.5" data-testid={`whatif-leave-${lv.id}`}>
                      <span className="text-xs text-violet-700 flex-1">
                        {s.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} — {e.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </span>
                      <Badge className="text-[9px] bg-violet-200 text-violet-800">{days}d</Badge>
                      <button onClick={() => removeWhatIfLeave(lv.id)} className="p-0.5 text-violet-400 hover:text-red-500" data-testid={`whatif-remove-${lv.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Chart */}
      <Card className="border-0 shadow-sm" data-testid="forecast-chart-card">
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Projected Balance</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  {/* Split fill gradients: series colour above the zero line, red below */}
                  <linearGradient id="gradApproved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={0} stopColor="#0284c7" stopOpacity={0.35} />
                    <stop offset={zeroOffset} stopColor="#0284c7" stopOpacity={0.04} />
                    <stop offset={zeroOffset} stopColor="#ef4444" stopOpacity={0.08} />
                    <stop offset={1} stopColor="#ef4444" stopOpacity={0.4} />
                  </linearGradient>
                  <linearGradient id="gradPending" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={0} stopColor="#f59e0b" stopOpacity={0.25} />
                    <stop offset={zeroOffset} stopColor="#f59e0b" stopOpacity={0.03} />
                    <stop offset={zeroOffset} stopColor="#ef4444" stopOpacity={0.06} />
                    <stop offset={1} stopColor="#ef4444" stopOpacity={0.3} />
                  </linearGradient>
                  <linearGradient id="gradWhatIf" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={0} stopColor="#7c3aed" stopOpacity={0.25} />
                    <stop offset={zeroOffset} stopColor="#7c3aed" stopOpacity={0.03} />
                    <stop offset={zeroOffset} stopColor="#ef4444" stopOpacity={0.06} />
                    <stop offset={1} stopColor="#ef4444" stopOpacity={0.3} />
                  </linearGradient>
                  {/* Split stroke gradients: line turns red once it crosses below zero */}
                  <linearGradient id="strokeApproved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={zeroOffset} stopColor="#0284c7" />
                    <stop offset={zeroOffset} stopColor="#ef4444" />
                  </linearGradient>
                  <linearGradient id="strokePending" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={zeroOffset} stopColor="#f59e0b" />
                    <stop offset={zeroOffset} stopColor="#ef4444" />
                  </linearGradient>
                  <linearGradient id="strokeWhatIf" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={zeroOffset} stopColor="#7c3aed" />
                    <stop offset={zeroOffset} stopColor="#ef4444" />
                  </linearGradient>
                  <linearGradient id="gradLastYear" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '11px' }} />
                <Legend wrapperStyle={{ fontSize: '9px' }} />
                <ReferenceLine y={0} stroke="#ef4444" strokeWidth={1.5} strokeDasharray="3 3" label={{ value: '0', position: 'left', fontSize: 9, fill: '#ef4444' }} />
                {/* Zero reference line for negatives */}
                <CartesianGrid horizontal={false} vertical={false} />
                {showLastYear && lastYear && (
                  <Area type="monotone" dataKey={`${lastYear.year} Actual`} stroke="#14b8a6" fill="url(#gradLastYear)" strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 2 }} />
                )}
                <Area type="monotone" dataKey="Approved Only" stroke="url(#strokeApproved)" fill="url(#gradApproved)" strokeWidth={2} dot={{ r: 3 }} />
                {hasPending && (
                  <Area type="monotone" dataKey="With Pending" stroke="url(#strokePending)" fill="url(#gradPending)" strokeWidth={1.5} strokeDasharray="5 5" dot={{ r: 2 }} />
                )}
                {hasWhatIf && (
                  <Area type="monotone" dataKey="What-If" stroke="url(#strokeWhatIf)" fill="url(#gradWhatIf)" strokeWidth={2} strokeDasharray="3 3" dot={{ r: 3, fill: '#7c3aed' }} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Month Table */}
      <Card className="border-0 shadow-sm" data-testid="forecast-table-card">
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Monthly Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-gray-500 font-medium">Month</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Accrual</th>
                  <th className="text-right py-2 text-rose-500 font-medium" title="Approved leave days consumed in this month (pending shown in brackets)">Used</th>
                  <th className="text-right py-2 text-sky-600 font-semibold">Balance</th>
                  {hasPending && <th className="text-right py-2 text-amber-600 font-semibold">Pending</th>}
                  {hasWhatIf && <th className="text-right py-2 text-violet-600 font-semibold">What-If</th>}
                  {showLastYear && lastYear && <th className="text-right py-2 text-teal-600 font-semibold">{lastYear.year}</th>}
                </tr>
              </thead>
              <tbody>
                {adjustedForecast.map((m, i) => {
                  const lyMonth = showLastYear && lastYear ? lastYear.months.find(lm => lm.month === m.month) : null;
                  const isNegApproved = m.balance_approved_only < 0;
                  const isNegPending = m.balance_with_pending < 0;
                  const isNegWhatIf = m.balance_what_if < 0;
                  const used = m.approved_deduction || 0;
                  const pendingUsed = m.pending_deduction || 0;
                  return (
                    <tr key={m.month} className={`border-b border-gray-50 ${m.is_past ? 'bg-gray-50/60' : i === 0 ? '' : ''}`} data-testid={`forecast-row-${m.month_name}`}>
                      <td className="py-2 font-medium text-gray-800">
                        {m.month_name}
                        {m.is_past && <span className="text-[8px] text-gray-400 ml-1">past</span>}
                      </td>
                      <td className="text-right py-2 text-emerald-600">{m.accrual > 0 ? `+${m.accrual}` : '-'}</td>
                      <td className="text-right py-2">
                        {used > 0 ? (
                          <span className="text-rose-600 font-semibold">−{used}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                        {pendingUsed > 0 && (
                          <span className="text-[9px] text-amber-600 ml-1" title="Pending leave days awaiting approval in this month">({pendingUsed}p)</span>
                        )}
                      </td>
                      <td className={`text-right py-2 font-semibold ${isNegApproved ? 'text-red-600' : 'text-sky-700'}`}>{m.balance_approved_only}</td>
                      {hasPending && <td className={`text-right py-2 font-semibold ${isNegPending ? 'text-red-600' : 'text-amber-700'}`}>{m.balance_with_pending}</td>}
                      {hasWhatIf && (
                        <td className={`text-right py-2 font-semibold ${isNegWhatIf ? 'text-red-600' : 'text-violet-700'}`}>
                          {m.balance_what_if}
                          {whatIfByMonth[m.month] > 0 && <span className="text-[9px] text-violet-400 ml-0.5">(-{whatIfByMonth[m.month]})</span>}
                        </td>
                      )}
                      {showLastYear && lastYear && <td className="text-right py-2 font-semibold text-teal-700">{lyMonth?.balance ?? '-'}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* End of Year Summary */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-sky-50 to-blue-50" data-testid="eoy-summary-card">
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-sky-600 uppercase tracking-wider mb-2">End of Year Projection</h3>
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div>
              <p className={`text-2xl font-bold ${endOfYear?.balance_approved_only < 0 ? 'text-red-600' : 'text-sky-700'}`}>{endOfYear?.balance_approved_only} <span className={`text-sm font-normal ${endOfYear?.balance_approved_only < 0 ? 'text-red-400' : 'text-sky-400'}`}>days</span></p>
              <p className="text-[10px] text-gray-500">Approved only</p>
            </div>
            {hasPending && (
              <div className="text-center">
                <p className={`text-lg font-bold ${endOfYear?.balance_with_pending < 0 ? 'text-red-600' : 'text-amber-600'}`}>{endOfYear?.balance_with_pending} <span className={`text-xs font-normal ${endOfYear?.balance_with_pending < 0 ? 'text-red-400' : 'text-amber-400'}`}>days</span></p>
                <p className="text-[10px] text-gray-500">If pending approved</p>
              </div>
            )}
            {hasWhatIf && (
              <div className="text-right">
                <p className={`text-lg font-bold ${endOfYear?.balance_what_if < 0 ? 'text-red-600' : 'text-violet-600'}`}>{endOfYear?.balance_what_if} <span className={`text-xs font-normal ${endOfYear?.balance_what_if < 0 ? 'text-red-400' : 'text-violet-400'}`}>days</span></p>
                <p className="text-[10px] text-gray-500">What-If scenario</p>
              </div>
            )}
          </div>
          {/* Carry-forward + LOP note (employee-facing) */}
          <div className="mt-3 pt-3 border-t border-sky-100 text-[11px]" data-testid="portal-eoy-note">
            {data.is_overdrawn ? (
              <p className="text-gray-600 leading-relaxed">
                <span className="font-semibold text-red-600">-{data.overdraw_lop_days} days</span> transferred to your LOP record
                <span className="mx-1 text-gray-300">·</span>
                <span className="font-semibold text-gray-800">0 days</span> carry forward to next year
              </p>
            ) : (
              <p className="text-gray-600">
                <span className="font-semibold text-gray-800">{data.carry_forward_next_year} days</span> will carry forward to {data.year + 1} (max 15)
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalLeaveForecast;
